import React, { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Plus, Star, Download, ChevronLeft, ChevronRight, CheckSquare, Archive, Trash2, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { api, downloadFile, errMsg } from "@/lib/api";
import { URGENCY } from "@/lib/brand";
import { EmptyState, PageHeader, SkeletonRows, StatusPill, useDateFmt } from "@/components/common";
import { useMeta } from "@/lib/hooks";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

const TABS = [
  { key: "all", label: "Active", params: { status: "active" } },
  { key: "overdue", label: "Overdue", params: { status: "active", urgency: "overdue" } },
  { key: "due_soon", label: "Due soon", params: { status: "active", urgency: "due_soon,due_today" } },
  { key: "favorites", label: "Pinned", params: { status: "active", favorite: true } },
  { key: "completed", label: "Completed", params: { status: "completed" } },
  { key: "archived", label: "Archived", params: { status: "archived" } },
];

export default function Reminders() {
  const [sp, setSp] = useSearchParams();
  const urlUrg = sp.get("urgency");
  const initialTab = urlUrg === "overdue" ? "overdue" : urlUrg ? "due_soon" : "all";
  const [tab, setTab] = useState(initialTab);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("");
  const [owner, setOwner] = useState(sp.get("owner_id") || "");
  const [sort, setSort] = useState("date");
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [sel, setSel] = useState([]);
  const [confirmDel, setConfirmDel] = useState(false);
  const { meta, entities } = useMeta();
  const f = useDateFmt();
  const params = { ...TABS.find((t) => t.key === tab).params, q, category_id: cat, owner_id: owner, sort, page, page_size: 20 };

  const load = useCallback(() => {
    api.get("/reminders", { params }).then(({ data }) => setData(data)).catch((e) => toast.error(errMsg(e)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, q, cat, owner, sort, page]);
  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [load]);
  useEffect(() => { setPage(1); setSel([]); }, [tab, q, cat, owner, sort]);

  const bulk = async (action) => {
    try {
      const { data: r } = await api.post("/reminders/bulk", { ids: sel, action });
      toast.success(`${r.affected} reminder${r.affected === 1 ? "" : "s"} updated`);
      setSel([]); load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const toggleFav = async (r) => {
    await api.patch(`/reminders/${r.id}`, { favorite: !r.favorite }).catch(() => {});
    load();
  };
  const exportFiltered = () => downloadFile("/excel/export", { ...params, page: undefined, page_size: undefined, sort: undefined, favorite: undefined }).catch(() => toast.error("Export failed"));
  const items = data?.items || [];
  const pages = data ? Math.max(1, Math.ceil(data.total / data.page_size)) : 1;
  const allSel = items.length > 0 && items.every((r) => sel.includes(r.id));

  return (
    <div data-testid="reminders-page">
      <PageHeader title="Reminders" subtitle="Search, filter and manage every date you're tracking." actions={<>
        <button className="pm-btn pm-btn-outline" onClick={exportFiltered} data-testid="reminders-export-btn"><Download className="w-4 h-4" /> Export filtered</button>
        <Link to="/reminders/new" className="pm-btn pm-btn-primary" data-testid="reminders-add-btn"><Plus className="w-4 h-4" /> Add Reminder</Link>
      </>} />
      <div className="flex gap-2 overflow-x-auto pb-1 mb-4" role="tablist">
        {TABS.map((t) => <button key={t.key} role="tab" className="pm-tab" data-active={tab === t.key} onClick={() => { setTab(t.key); setSp({}); }} data-testid={`reminders-tab-${t.key}`}>{t.label}</button>)}
      </div>
      <div className="pm-card p-4 sm:p-5">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
          <input className="pm-input" placeholder="Search title, notes, tags…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="reminders-search-input" />
          <select className="pm-input" value={cat} onChange={(e) => setCat(e.target.value)} data-testid="reminders-category-filter">
            <option value="">All categories</option>{meta?.categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <select className="pm-input" value={owner} onChange={(e) => setOwner(e.target.value)} data-testid="reminders-owner-filter">
            <option value="">All people & assets</option>{entities.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
          </select>
          <select className="pm-input" value={sort} onChange={(e) => setSort(e.target.value)} data-testid="reminders-sort-select">
            <option value="date">Soonest first</option><option value="-date">Latest first</option><option value="title">Title A–Z</option><option value="category">Category</option><option value="-created">Recently added</option>
          </select>
        </div>
        {sel.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 rounded-xl bg-[#F2EFFC] p-2.5 mb-3" data-testid="bulk-actions-bar">
            <span className="text-sm font-semibold px-2">{sel.length} selected</span>
            <button className="pm-btn pm-btn-sm pm-btn-outline" onClick={() => bulk("complete")} data-testid="bulk-complete-btn"><CheckSquare className="w-4 h-4" />Complete</button>
            <button className="pm-btn pm-btn-sm pm-btn-outline" onClick={() => bulk("archive")} data-testid="bulk-archive-btn"><Archive className="w-4 h-4" />Archive</button>
            <button className="pm-btn pm-btn-sm pm-btn-outline" onClick={() => bulk("activate")} data-testid="bulk-activate-btn"><RotateCcw className="w-4 h-4" />Set active</button>
            <button className="pm-btn pm-btn-sm pm-btn-outline text-[var(--pm-danger)]" onClick={() => setConfirmDel(true)} data-testid="bulk-delete-btn"><Trash2 className="w-4 h-4" />Delete</button>
          </div>
        )}
        {!data ? <SkeletonRows /> : items.length === 0 ? (
          <EmptyState title="No reminders here" text={q || cat || owner ? "Try clearing your filters." : "Add a reminder to start your countdown."}
            action={<Link to="/reminders/new" className="pm-btn pm-btn-primary"><Plus className="w-4 h-4" />Add Reminder</Link>} />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="reminders-table">
              <thead><tr className="text-left text-xs text-[var(--pm-muted)] border-b border-[var(--pm-border)]">
                <th className="py-2.5 w-8"><input type="checkbox" aria-label="Select all" checked={allSel} onChange={() => setSel(allSel ? [] : items.map((r) => r.id))} className="accent-[var(--pm-primary)]" data-testid="select-all-checkbox" /></th>
                <th className="py-2.5 w-8" /><th className="py-2.5">Reminder</th><th className="py-2.5 hidden md:table-cell">Owner</th><th className="py-2.5 hidden sm:table-cell">Date</th><th className="py-2.5">Countdown</th>
              </tr></thead>
              <tbody>
                {items.map((r) => (
                  <tr key={r.id} className="border-b border-[var(--pm-border)] last:border-0 hover:bg-[#FAF9FF]" data-testid={`reminder-tr-${r.id}`}>
                    <td className="py-3"><input type="checkbox" aria-label={`Select ${r.title}`} checked={sel.includes(r.id)} onChange={() => setSel(sel.includes(r.id) ? sel.filter((x) => x !== r.id) : [...sel, r.id])} className="accent-[var(--pm-primary)]" data-testid={`select-${r.id}`} /></td>
                    <td><button onClick={() => toggleFav(r)} aria-label="Pin" data-testid={`fav-${r.id}`}><Star className={`w-4 h-4 ${r.favorite ? "fill-[#F6AA3E] text-[#F6AA3E]" : "text-[#C9C5D8]"}`} /></button></td>
                    <td className="py-3 pr-3"><Link to={`/reminders/${r.id}`} className="font-semibold hover:text-[var(--pm-primary)]" data-testid={`reminder-link-${r.id}`}>{r.title}</Link>
                      <p className="text-xs text-[var(--pm-muted)]">{r.category_name} → {r.subcategory_name}{r.attachment_count ? ` · ${r.attachment_count} file${r.attachment_count > 1 ? "s" : ""}` : ""}{r.tags?.length ? ` · #${r.tags.join(" #")}` : ""}</p></td>
                    <td className="hidden md:table-cell text-[var(--pm-muted)]">{r.owner_name || "—"}</td>
                    <td className="hidden sm:table-cell text-[var(--pm-muted)]">{f(r.next_date)}</td>
                    <td><StatusPill urgency={r.urgency}>{r.status === "active" ? r.countdown : URGENCY[r.status].label}</StatusPill></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {data && data.total > data.page_size && (
          <div className="flex items-center justify-between mt-4 text-sm" data-testid="pagination">
            <span className="text-[var(--pm-muted)]">{data.total} reminders · page {page} of {pages}</span>
            <div className="flex gap-2">
              <button className="pm-btn pm-btn-sm pm-btn-outline" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="page-prev"><ChevronLeft className="w-4 h-4" /></button>
              <button className="pm-btn pm-btn-sm pm-btn-outline" disabled={page >= pages} onClick={() => setPage(page + 1)} data-testid="page-next"><ChevronRight className="w-4 h-4" /></button>
            </div>
          </div>
        )}
      </div>
      <AlertDialog open={confirmDel} onOpenChange={setConfirmDel}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Delete {sel.length} reminder{sel.length === 1 ? "" : "s"}?</AlertDialogTitle>
            <AlertDialogDescription>This permanently removes the reminders, their notifications, renewal history and attached files.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction className="bg-[var(--pm-danger)]" onClick={() => bulk("delete")} data-testid="bulk-delete-confirm">Delete</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
